/** Shared constants and types for the AI subsystem modules. */
import { TILE_SIZE } from '../utils/Constants';

// --- AI State type (shared across subsystems) ---
export type AIState = 'duty' | 'alert' | 'chase' | 'search' | 'stagger' | 'using_skill';
export type DutySubstate = 'patrol' | 'patrol-stalled' | 'wander' | 'passive' | 'investigate';

// --- Movement result ---
export interface MovementResult {
  vx: number;
  vy: number;
  wantsAttack: boolean;
}

/** Sentinel zero-movement result — returned when the AI is blocked, waiting, or in a non-movement state. */
export const ZERO_MOVEMENT: MovementResult = { vx: 0, vy: 0, wantsAttack: false };

// --- Pathfinding ---

/** How often (ms) to recalculate a cached A* path. Lower values track moving targets better but cost more. */
export const PATH_RECALC_INTERVAL = 500;

/** Waypoint arrival radius (px). Enemy advances to the next waypoint when within this distance. */
export const PATH_WAYPOINT_THRESHOLD = 8;

/** Squared form of PATH_WAYPOINT_THRESHOLD — avoids a sqrt per tick in waypoint following. */
export const PATH_WAYPOINT_THRESHOLD_SQ = PATH_WAYPOINT_THRESHOLD * PATH_WAYPOINT_THRESHOLD;

/** Maximum A* node budget used when an enemy's pathfinding has been escalated past the normal
 *  distance-scaled threshold. Shared by ChaseSystem and ReturnToPostBehavior. */
export const ESCALATED_A_STAR_BUDGET = 2000;

/** A* budget floor (short paths, nearby targets). */
const A_STAR_BUDGET_MIN = 200;
/** A* budget ceiling (long commutes, cross-map paths). */
const A_STAR_BUDGET_MAX = 2000;
/** Extra A* nodes per tile of world distance. */
const A_STAR_BUDGET_PER_TILE = 20;

/** Scale the initial A* node budget by world-space distance so short paths
 *  stay cheap while longer paths get enough room to navigate tight corridors. */
export function scaledAStarBudget(worldDist: number): number {
  const tileDist = worldDist / TILE_SIZE;
  return Math.min(A_STAR_BUDGET_MAX, Math.max(A_STAR_BUDGET_MIN, A_STAR_BUDGET_MIN + tileDist * A_STAR_BUDGET_PER_TILE));
}

// --- Aggro ---

/** Aggro decay multiplier applied when the enemy is in search state — faster forgetting without LOS. */
export const SEARCH_DECAY_MULTIPLIER = 0.5;

/** Aggro high-water-mark threshold (0–100) that triggers the alert state on crossing. */
export const ALERT_HWM_THRESHOLD = 75;

/** Minimum aggro level (0–100) for an enemy to count as "noisy" during alert propagation sweeps.
 *  Enemies at or below this floor are excluded to prevent perpetual re-alert loops in calm rooms. */
export const ALERT_AGGRO_FLOOR = 20;

/** How long (ms) an enemy holds the alert state before timing out back to duty. */
export const ALERT_DURATION_MS = 60000;

/** Aggro decay never fully reaches zero — enemies at this floor remain slightly suspicious.
 *  Expressed as a fraction of the per-frame decay rate. */
export const AGGRO_DECAY_FLOOR = 0.25;

// --- Duty ---

/** Distance (px) from the duty post within which the enemy is considered "returned" and stops pathfinding. */
export const DUTY_RETURN_THRESHOLD = 48;

/** Margin (px) kept between wander targets and the outer duty return threshold — prevents hugging the boundary. */
export const WANDER_MARGIN = 12; // keep wander targets this far inside return threshold

/** Interval (ms) between stuck escalation stages. Each stage tries a progressively
 *  more aggressive escape strategy (budget bump → reverse path → nudge → terminal).
 *  Kept short because each stage is cheap — the real cost is leaving an enemy
 *  visibly stuck. Four stages at 250ms = 1 second worst case to terminal. */
export const STUCK_ESCALATION_INTERVAL_MS = 250;

/** Minimum distance improvement (px) required to reset the stuck timer and count as forward progress. */
export const DUTY_STUCK_PROGRESS = 16;

/** Squared arrival threshold (px²) for wander waypoints — avoids a sqrt each frame. */
export const WANDER_ARRIVAL_DIST_SQ = 16;

/** Base pause duration (ms) between wander legs — total pause is base + random. */
export const WANDER_PAUSE_BASE_MS = 800;

/** Random additional pause (ms) added on top of the base — gives wander a natural, uneven cadence. */
export const WANDER_PAUSE_RANDOM_MS = 1200;

/** How long (ms) a wander leg can go without progress before the waypoint is abandoned. */
export const WANDER_STUCK_TIMEOUT_MS = 1500;

/** Squared minimum progress (px²) per wander stuck window — below this, the leg is considered stalled. */
export const WANDER_STUCK_PROGRESS_SQ = 4 * 4;

// Duty substates

/** Default duration (ms) of a patrol substate cycle before the enemy may switch to wander. */
export const DEFAULT_PATROL_DURATION_MS = 90000;

/** Default duration (ms) of a wander substate cycle before the enemy may switch to patrol. */
export const DEFAULT_WANDER_DURATION_MS = 120000;

/** Default patrol radius (px) — waypoints are chosen within this distance of the duty post. */
export const DEFAULT_PATROL_RADIUS = 192;

/** Minimum squared distance (px²) between consecutive patrol waypoints — prevents backtracking in place. */
export const PATROL_WAYPOINT_MIN_DIST_SQ = (TILE_SIZE * 3) * (TILE_SIZE * 3);

/** Minimum squared distance (px²) from the duty post for patrol waypoints — keeps them spread around the post. */
export const PATROL_WAYPOINT_MIN_POST_DIST_SQ = (TILE_SIZE * 1) * (TILE_SIZE * 1); // min distance from duty post

/** Squared arrival threshold (px²) for patrol waypoints — enemy advances to the next leg on crossing. */
export const PATROL_WAYPOINT_ARRIVAL_DIST_SQ = (TILE_SIZE * 1.5) * (TILE_SIZE * 1.5);

/** Base pause duration (ms) between patrol legs. */
export const PATROL_PAUSE_BASE_MS = 1500;

/** Random additional pause (ms) on top of base — total pause is 1.5–3s per waypoint. */
export const PATROL_PAUSE_RANDOM_MS = 1500; // total pause = 1.5–3s

/** Minimum interval (ms) between passive-substate wake cycles — enemy is dormant for at least this long. */
export const DEFAULT_PASSIVE_WAKE_MIN_MS = 60000;   // 1 min min wake interval

/** Maximum interval (ms) between passive-substate wake cycles — wake time is random within [min, max]. */
export const DEFAULT_PASSIVE_WAKE_MAX_MS = 180000;  // 3 min max wake interval

/** How long (ms) a patrol-stalled enemy patrols at its temp position before retrying the original post (ADR-059). */
export const STALL_RETRY_DELAY_MS = 30000;          // 30s patrol at temp post before retrying original

// --- Corpse ---

/** How long (ms) a corpse record persists in EnemyManager before being cleaned up. */
export const DEFAULT_CORPSE_DURATION_MS = 120_000; // 2 min

/** Duration (ms) after taking damage during which the AI considers itself "recently damaged" —
 *  suppresses certain duty transitions and enables reactive skill conditions (block, etc.). */
export const RECENTLY_DAMAGED_WINDOW_MS = 2000;

// --- Investigation ---

/** Detection radius (px) within which an enemy notices a corpse and begins investigating. */
export const INVESTIGATE_RADIUS = TILE_SIZE * 8; // 256px — how far an enemy will notice a corpse

/** Minimum stop distance from a corpse (in tiles) — enemy halts investigation at least this far away. */
export const INVESTIGATE_STOP_MIN_TILES = 1;

/** Maximum stop distance from a corpse (in tiles) — enemy stops somewhere between min and max. */
export const INVESTIGATE_STOP_MAX_TILES = 4;

// --- Combat alert propagation ---

/** Radius (px) within which nearby allies receive an aggro bump when combat begins. */
export const COMBAT_ALERT_RADIUS = TILE_SIZE * 6; // 192px / 6 tiles

/** Aggro amount added to nearby allies during a combat sound alert. */
export const COMBAT_ALERT_AGGRO_AMOUNT = 30;

// --- Enemy-to-enemy alert propagation ---

/** How often (ms) EnemyManager sweeps for alert-state enemies to propagate their tension to nearby allies. */
export const ALERT_PROPAGATION_INTERVAL_MS = 500;

/** Fraction of the source enemy's aggro passed to a receiving ally — prevents propagation from amplifying. */
export const ALERT_RELAY_ATTENUATION = 0.9;

// --- Facing ---

/** Angular lerp speed (radians/s) for smoothing facing direction toward the target angle. */
export const FACING_LERP_SPEED = 12;

/** Distance (px) used for wall-avoidance raycast when deciding which direction to face while idle. */
export const WALL_FACE_CHECK_DIST = TILE_SIZE * 2;

/** Delay (ms) after the enemy stops moving before wall-avoidance facing kicks in — avoids jitter on short stops. */
export const IDLE_WALL_CHECK_DELAY_MS = 500; // wait before wall-avoidance after stopping

/** Head-swivel sweep amplitude (degrees) during idle patrol facing — subtle awareness scan. */
export const IDLE_LOOK_SWEEP_DEG = 30;

/** Head-swivel period (ms) for one full sweep cycle in idle/patrol state. */
export const IDLE_LOOK_PERIOD_MS = 3200;

/** Head-swivel sweep amplitude (degrees) during search state — broader scan for a lost target. */
export const SEARCH_LOOK_SWEEP_DEG = 45;

/** Head-swivel period (ms) for one full sweep cycle in search state. */
export const SEARCH_LOOK_PERIOD_MS = 1800;

/** Head-swivel sweep amplitude (degrees) during alert state — wide arc for a known threat direction. */
export const ALERT_LOOK_SWEEP_DEG = 90;

/** Head-swivel period (ms) for one full sweep cycle in alert state. */
export const ALERT_LOOK_PERIOD_MS = 2400;

/** Head-swivel sweep amplitude (degrees) during passive substate — minimal movement, low awareness. */
export const PASSIVE_LOOK_SWEEP_DEG = 15;

/** Head-swivel period (ms) for one full sweep cycle in passive substate. */
export const PASSIVE_LOOK_PERIOD_MS = 4800;

// --- Search ---

/** Minimum cooldown (ms) after leaving chase before re-entering chase from any state.
 *  Prevents tight chase↔search and duty↔chase oscillation when aggro hovers near the
 *  distance-scaled chase threshold or LOS flickers at geometry edges. */
export const CHASE_REENTRY_COOLDOWN_MS = 500;

/** Maximum number of positions remembered in the search memory ring buffer. */
export const SEARCH_MEMORY_SIZE = 8;

/** Squared distance (px²) threshold for deduplicating search memory entries — nearby positions merge. */
export const SEARCH_MEMORY_DIST_SQ = (TILE_SIZE * 5) * (TILE_SIZE * 5);

/** Radius (tile units) scanned for exit candidates when the player is lost at an LKP. */
export const EXIT_SCAN_RADIUS = 8;

/** Squared minimum distance (px²) an exit candidate must be from the enemy's position to qualify. */
export const EXIT_MIN_DIST_SQ = (TILE_SIZE * 2) * (TILE_SIZE * 2);

/** Minimum number of exit candidates sampled when predicting the player's escape route. */
export const EXIT_BASE_CANDIDATES = 2;

/** Additional exit candidates considered when aggro HWM is high — more thorough pursuit at high tension. */
export const EXIT_BONUS_CANDIDATES = 4;

/** Minimum number of search legs generated for a new search pattern. */
export const SEARCH_BASE_LEGS = 3;

/** Additional search legs added at high aggro HWM — enemy searches more thoroughly when recently spotted. */
export const SEARCH_BONUS_LEGS = 4;

/** Dot-product threshold below which a direction component is rounded to zero during quantization.
 *  Prevents near-cardinal movement from producing diagonal search legs. */
export const DIR_QUANTIZE_THRESHOLD = 0.3;

// --- Tile walkability ---

/** A* cost multiplier for buffer-blocked tiles. Enemies can walk through buffer
 *  zones but prefer non-buffer paths of similar length. A value of 3 means one
 *  buffer tile costs the same as three normal tiles — enemies will detour up to
 *  ~2 extra tiles to avoid a buffer tile, but won't reroute around entire buildings. */
export const BUFFER_TILE_COST_MULTIPLIER = 3;

/** Check if a tile blocks AI movement. Only checks true collision (walls,
 *  rubble, void). Buffer zones are walkable — the A* pathfinder applies a cost
 *  penalty instead of treating them as walls. */
export function tileBlocksMovement(tile: Phaser.Tilemaps.Tile | null): boolean {
  if (!tile) return true;
  return tile.collides;
}

/** Strict walkability check that also excludes building buffer zones.
 *  Used for spawn validation and facing direction — don't spawn enemies
 *  inside buffer zones or let them stare into building edges. */
export function tileBlocksMovementStrict(tile: Phaser.Tilemaps.Tile | null): boolean {
  if (!tile) return true;
  return tile.collides || !!tile.properties?.bufferBlocked;
}

// --- Direction arrays (hoisted to avoid per-call allocation) ---

/** Cardinal direction unit vectors (left, right, up, down) — used for tile neighbor iteration in A*. */
export const CARDINAL_DIRS: readonly [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];

/** All eight neighbor directions (cardinal + diagonal) — used for broader tile traversal and exit scanning. */
export const ALL_DIRS: readonly { dx: number; dy: number }[] = [
  { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
  { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
  { dx: 1, dy: 1 }, { dx: -1, dy: 1 },
  { dx: 1, dy: -1 }, { dx: -1, dy: -1 },
];
